import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCompass, FaDownload, FaMap, FaRocket, FaCogs, FaUsers, FaBrain, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { Footer } from '../components/shared/Footer';

const AIReadinessAssessment = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const TakeAssessmentButton = ({ className = "" }) => (
    <Link to="/services/ai-assessment-form">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all text-white shadow-lg hover:shadow-xl ${className}`}
      >
        <FaCompass className="text-lg" />
        Take Assessment
      </motion.button>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto px-6 py-16"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <FaCompass className="text-5xl text-teal-400 mr-4" />
            <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-blue-400">
              AI Strategy & Readiness Assessment
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            A comprehensive framework to evaluate your organization's AI maturity and build a roadmap for transformation. Use this tool independently or share it with your team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <TakeAssessmentButton />
            <div className="text-sm text-gray-400">
              <FaCheckCircle className="inline mr-2 text-green-400" />
              No signup required • Free to use • Share freely
            </div>
          </div>
        </motion.div>

        {/* Overview */}
        <motion.section variants={itemVariants} className="mb-16">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-teal-400">Overview</h2>
            <div className="text-lg leading-relaxed space-y-4">
              <p className="text-gray-300">
                This framework helps organizations evaluate their readiness for AI integration and transformation. It provides a structured approach for understanding where you currently stand and what steps are needed to move forward.
              </p>
              <p className="text-gray-300">
                Use this assessment independently to gauge your maturity level and identify areas for investment and development. When ready, we can help refine, validate, and guide your next stages of implementation.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Purpose */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-blue-400">Purpose of the Assessment</h2>
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <p className="text-lg text-gray-300 mb-6">The AI Strategy and Readiness Assessment helps organizations:</p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                Understand their current level of AI maturity.
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                Identify strengths, gaps, and opportunities in data, technology, people, and governance.
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                Prioritize actions aligned with business strategy and capability.
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                Build a roadmap for responsible and scalable AI integration.
              </li>
            </ul>
          </div>
        </motion.section>

        {/* Maturity Model */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-purple-400">The Maturity Model</h2>
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 mb-8">
            <p className="text-lg text-gray-300">
              The assessment is based on a progressive model of AI capability — from simple access to full-scale operational transformation. Each stage represents a step in the evolution of how AI supports organizational value creation.
            </p>
          </div>

          {/* Stage 1 */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <FaRocket className="text-2xl text-blue-400 mr-3" />
                <h3 className="text-2xl font-bold text-blue-400">Stage 1 – AI Access (AI + Resources)</h3>
              </div>
              <p className="text-gray-300 mb-4">Foundational access to AI systems, models, and data. The organization is experimenting and learning.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Indicators:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Use of public or enterprise AI tools (e.g., ChatGPT, Copilot, 365 Copilot)</li>
                    <li>• Limited governance or defined strategy</li>
                    <li>• Exploration of retrieval-augmented generation (RAG) systems</li>
                    <li>• Early pilots of AI trained on organizational data</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Readiness Questions:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Who currently uses AI, and for what tasks?</li>
                    <li>• What organizational data could enhance AI relevance?</li>
                    <li>• Is there a clear data access and security policy?</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Stage 2 */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-500/30 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <FaCogs className="text-2xl text-green-400 mr-3" />
                <h3 className="text-2xl font-bold text-green-400">Stage 2 – AI Integration (AI + Tools)</h3>
              </div>
              <p className="text-gray-300 mb-4">AI capabilities become embedded within systems and workflows to enhance productivity and collaboration.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Indicators:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• AI copilots integrated into productivity tools (GitHub Copilot in VS Code, Microsoft 365 Copilot in Outlook/Word, Google Workspace AI)</li>
                    <li>• AI features activated in business systems (Salesforce Einstein for lead scoring, ServiceNow Virtual Agent for IT tickets, HubSpot AI for content generation)</li>
                    <li>• Simple automation within single platforms (AI-powered approvals in workflow tools, automated data extraction from forms)</li>
                    <li>• Defined roles and responsibilities for AI oversight</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Readiness Questions:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Which systems already include AI features, and are they activated?</li>
                    <li>• Are employees trained to use AI assistance effectively in their daily tools?</li>
                    <li>• Do you have governance for what AI features can/cannot be used?</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Stage 3 */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <FaMap className="text-2xl text-purple-400 mr-3" />
                <h3 className="text-2xl font-bold text-purple-400">Stage 3 – AI Orchestration (AI + Workflows)</h3>
              </div>
              <p className="text-gray-300 mb-4">The organization coordinates AI and automation through orchestration systems, with AI handling multi-step processes under human oversight.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Indicators:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Implementation of workflow orchestrators (e.g., n8n, Power Automate)</li>
                    <li>• Specialized agents performing multi-step logic (LangChain, MCP)</li>
                    <li>• Cross-system coordination with clear human oversight</li>
                    <li>• AI handling process routing and task coordination</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Readiness Questions:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Are there defined workflows where AI triggers or executes actions?</li>
                    <li>• How is responsibility shared between humans and AI agents?</li>
                    <li>• Are governance and monitoring systems in place for automated actions?</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Stage 4 */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <FaBrain className="text-2xl text-orange-400 mr-3" />
                <h3 className="text-2xl font-bold text-orange-400">Stage 4 – AI Transformation (AI + Mission)</h3>
              </div>
              <p className="text-gray-300 mb-4">AI is systematically integrated across core operations. AI provides scaled execution and intelligence while humans maintain strategic decision authority and accountability.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Indicators:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Processes redesigned for human-AI collaboration, with AI handling execution at scale</li>
                    <li>• AI provides predictive and prescriptive insights in real time</li>
                    <li>• Continuous learning loops integrate feedback from data and outcomes</li>
                    <li>• Strong AI governance, ethics, and compliance frameworks</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Readiness Questions:</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Is AI embedded in the organization's strategic planning?</li>
                    <li>• How does leadership use AI for decision support?</li>
                    <li>• What governance ensures responsible and ethical AI deployment?</li>
                    <li>• Can you clearly explain accountability when AI is involved in decisions?</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Scoring and Self-Evaluation */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-cyan-400">Scoring and Self-Evaluation</h2>
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <p className="text-lg text-gray-300 mb-6">Each stage can be evaluated across the following dimensions:</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-2">Strategy</h4>
                <p className="text-sm text-gray-300">Is AI aligned with business objectives?</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-2">Data</h4>
                <p className="text-sm text-gray-300">Are data sources integrated, secure, and AI-ready?</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-2">Technology</h4>
                <p className="text-sm text-gray-300">Are tools, infrastructure, and models scalable?</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-2">People</h4>
                <p className="text-sm text-gray-300">Are teams trained and empowered to use AI effectively?</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-2">Governance</h4>
                <p className="text-sm text-gray-300">Are oversight, ethics, and compliance in place?</p>
              </div>
            </div>
            <div className="mt-6 p-4 bg-gradient-to-r from-teal-600/20 to-blue-600/20 border border-teal-500/30 rounded-lg">
              <p className="text-gray-300">
                <strong className="text-white">Scoring:</strong> Use a <strong className="text-white">1–5 scale</strong> (1 = Early Exploration, 5 = Fully Operational) to assess maturity across these dimensions. The resulting profile highlights readiness strengths and areas needing development.
              </p>
            </div>
          </div>
        </motion.section>

        {/* How to Use */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-indigo-400">How to Use This Framework</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-3">Self-assess</h4>
              <p className="text-gray-300">Review each stage and score your organization across the five dimensions.</p>
            </div>
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-3">Identify Gaps</h4>
              <p className="text-gray-300">Pinpoint areas where your current practices fall short of your strategic goals.</p>
            </div>
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-3">Plan Actions</h4>
              <p className="text-gray-300">Outline key initiatives to advance to the next stage.</p>
            </div>
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-3">Engage Partners</h4>
              <p className="text-gray-300">When ready, reach out for guidance on implementation, governance, or architecture.</p>
            </div>
          </div>
        </motion.section>

        {/* Outcome */}
        <motion.section variants={itemVariants} className="mb-16">
          <div className="bg-gradient-to-r from-teal-600/20 to-blue-600/20 border border-teal-500/30 rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-teal-400">Outcome</h2>
            <p className="text-lg text-gray-300">
              This assessment provides a clear view of where you are today and where you can go next. By using it, organizations can build confidence, clarity, and alignment in their AI journey — turning potential into operational advantage.
            </p>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section variants={itemVariants} className="text-center">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <h2 className="text-2xl font-bold mb-4">Ready to Assess Your AI Readiness?</h2>
            <p className="text-gray-300 mb-6">
              Download the complete framework and start your AI transformation journey today. Share it freely with your team and colleagues.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <TakeAssessmentButton />
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-4 border border-gray-500 rounded-lg font-semibold hover:border-gray-400 transition-all text-white"
              >
                Get Implementation Help <FaArrowRight className="text-sm" />
              </motion.a>
            </div>
          </div>
        </motion.section>
      </motion.div>
      <Footer />
    </div>
  );
};

export default AIReadinessAssessment;
